import { BetChangeActionBragg } from "@spawn/backend-adapter-bragg";
import { consumer, EventDispatcher, inject } from "@spawn/core";
import { INetworkStateSlotGameBase, PaylineType } from "@spawn/networking";
import { generateBraggNudgingOffsets } from "@spawn/nudge";
import {
	BraggSlotfaceRestoreService,
	ensurePaddingMysteries,
	generateBraggRandomSlotfaceForLevelData,
	getRevealTarget,
	HighlightEvents,
	parseRevealBraggNetworkState,
	replaceRevealPositionsWithMysteries,
	RevealEvents,
	RevealNetworkState,
} from "@spawn/reveal";
import { GameConfigService, replaceSymbolEvent } from "@spawn/slot-machine";
import { laglessSpin } from "../../lagless/laglessSpinConfig";
import { BaseSymbolTypes, symbolTypeBraggIds, SymbolTypes } from "../../services/symbolService";
import { UncoverTierService } from "../../services/uncoverTierService";
import { IGameNetworkState } from "../networkState";
import { nrgApplyBraggSlotfaceBeforeRestore } from "./helpers";

/** Bet change action for oryx backend. */
@consumer
export class ExtendedBetChangeActionBragg extends BetChangeActionBragg {
	@inject protected readonly uncoverTierService!: UncoverTierService;
	@inject protected readonly braggSlotfaceRestoreService!: BraggSlotfaceRestoreService;
	@inject protected readonly eventDispatcher!: EventDispatcher;
	@inject protected readonly gameConfigService!: GameConfigService;

	/**
	 * Restores the changed slot-face after the bet has changed according to the given
	 * network states.
	 *
	 * @param network Slotgame network state.
	 * @param reveal Reveal network state, if any.
	 */
	private restore(network: INetworkStateSlotGameBase, reveal?: RevealNetworkState): void {
		const { swingOnSymbols, swingOffSymbols } = this.gameConfigService.defaultConfig();

		for (let x = 0; x < network.reels.length; x++) {
			// Cut off swing-off symbol.
			const symbols = network.reels[x].length - (swingOffSymbols + swingOnSymbols);

			for (let y = 0; y < symbols; y++) {
				let type = network.reels[x][y + swingOffSymbols];

				if (reveal) {
					const target = getRevealTarget(reveal, x, y);

					if (target) {
						type = target.symbol;

						this.eventDispatcher.dispatch({
							type: HighlightEvents.EnableHighlightFeature,
							payload: {
								target: {
									symbol: target.symbol,
									position: { x: target.position.x, y: target.position.y },
									uncoverTier: target.uncoverTier,
								},
							},
						});
					}
				}

				const coordinate = { x, y };

				this.eventDispatcher.dispatch(
					replaceSymbolEvent({
						symbolType: type,
						coordinate,
					})
				);
			}
		}
	}

	/** @inheritdoc */
	protected parse({ nextBet, nextPaylineCount }: { nextBet: number; nextPaylineCount: number }): IGameNetworkState {
		this.eventDispatcher.dispatch({ type: HighlightEvents.ResetHighlightFeature });

		const result = super.parse({ nextBet, nextPaylineCount });

		let reveal;
		let nudging;

		let levelData = this.braggSlotfaceRestoreService.getLevelData(String(result.slotGameBase.selectedBet));

		if (!levelData) {
			const symbolsList = Array.from(laglessSpin.symbols.get(1)!).filter(
				(symbol: SymbolTypes) => symbol !== BaseSymbolTypes.Mystery
			);

			levelData = {
				level: String(result.slotGameBase.selectedBet),
				reveals: [],
				slotFace: generateBraggRandomSlotfaceForLevelData(result.slotGameBase.reels, symbolsList, symbolTypeBraggIds),
			};

			this.braggSlotfaceRestoreService.setLevelData(levelData);
		}

		if (levelData) {
			nrgApplyBraggSlotfaceBeforeRestore(result.slotGameBase.reels, levelData.slotFace, symbolTypeBraggIds);
			const uncoverTier = this.uncoverTierService.fromWinFactor(result.slotGameBase.winFactor);

			if (levelData.reveals?.length) {
				ensurePaddingMysteries(result.slotGameBase.reels, levelData.reveals, BaseSymbolTypes.Mystery);
				replaceRevealPositionsWithMysteries(result.slotGameBase.reels, levelData.reveals, BaseSymbolTypes.Mystery);

				reveal = parseRevealBraggNetworkState(levelData.reveals, symbolTypeBraggIds, uncoverTier);
				nudging = generateBraggNudgingOffsets(levelData.reveals);
			}

			this.restore(result.slotGameBase, reveal);
		}

		this.eventDispatcher.dispatch({
			type: RevealEvents.UpdateRevealSlotface,
			payload: {
				reels: result.slotGameBase.reels.map((reel) => reel.slice(1, -1)),
			},
		});

		return {
			...result,
			slotGameBase: {
				...result.slotGameBase,
				paylines: result.slotGameBase.paylines.filter(({ type }) => type !== PaylineType.Scatter),
			},
			reveal,
			nudging,
		};
	}
}
